import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { setModalOpen, setModalState, modalViewsConst } from "../../store/modalSlice";
import { saveShippingAddress, ShippingForm } from "../../store/clubChecklistSlice";
import { usePostHogCapture } from "../../hooks/usePostHogCapture";
import { POSTHOG_EVENTS } from "../../utils/posthogEvents";
import ModalHeader from "./ModalHeader";
import { ReactComponent as GiftIcon } from "../../assets/icons/gift-new.svg";
import { AppDispatch } from "../../store";

const HatUnlockModal = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { capture } = usePostHogCapture();
  const [screen, setScreen] = useState<"celebrate" | "address" | "done">("celebrate");
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState<ShippingForm>({
    name: "",
    address1: "",
    address2: "",
    city: "",
    state: "",
    zip: "",
    country: "",
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitError(null);
    setSubmitting(true);
    const result = await dispatch(saveShippingAddress(form));
    setSubmitting(false);
    if (saveShippingAddress.rejected.match(result)) {
      setSubmitError("Failed to save your address. Please try again.");
      return;
    }
    capture(POSTHOG_EVENTS.CHECKLIST_ADDRESS_SUBMITTED);
    setScreen("done");
    dispatch(setModalState({ modalOpen: true, modalView: modalViewsConst.hatUnlockDone }));
  };

  const handleClose = () => dispatch(setModalOpen(false));

  if (screen === "celebrate") {
    return (
      <div className="flex flex-col">
        <ModalHeader
          title="You unlocked a free hat!"
          icon={<GiftIcon className="h-5 w-5 text-eai-primary-accent" style={{ "--stroke-0": "currentColor" } as any} />}
          onClose={handleClose}
        />
        <div className="flex grow flex-col p-5 items-center text-center gap-4">
          <div className="text-5xl">🎉</div>
          <p className="text-sm text-gray-500 max-w-xs">
            You've completed the checklist. Enter your shipping address and we'll send your hat right to you.
          </p>
          <div className="flex gap-3 w-full">
            <button
              onClick={handleClose}
              className="flex-1 eai-btn text-sm font-Inter"
            >
              I'll do this later
            </button>
            <button
              onClick={() => setScreen("address")}
              className="flex-1 eai-btn-grn text-sm font-Inter"
            >
              Claim my hat
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (screen === "address") {
    return (
      <div className="flex flex-col">
        <ModalHeader
          title="Shipping address"
          icon={<GiftIcon className="h-5 w-5 text-eai-primary-accent" style={{ "--stroke-0": "currentColor" } as any} />}
          onClose={handleClose}
        />
        <form onSubmit={handleSubmit} className="flex grow flex-col p-5">
          <div className="text-gray-400 text-md mb-5 font-normal font-Inter">
            Where should we send your hat?
          </div>

          <div className="text-gray-500 text-sm mb-2 font-normal font-['Inter'] leading-[18px]">
            Full name
          </div>
          <div className="flex grow">
            <input
              name="name"
              placeholder="Full name"
              value={form.name}
              onChange={handleChange}
              required
              className="flex grow border p-3 mb-5 text-md rounded-md"
            />
          </div>

          <div className="text-gray-500 text-sm mb-2 font-normal font-['Inter'] leading-[18px]">
            Address line 1
          </div>
          <div className="flex grow">
            <input
              name="address1"
              placeholder="Street address"
              value={form.address1}
              onChange={handleChange}
              required
              className="flex grow border p-3 mb-5 text-md rounded-md"
            />
          </div>

          <div className="text-gray-500 text-sm mb-2 font-normal font-['Inter'] leading-[18px]">
            Address line 2 (optional)
          </div>
          <div className="flex grow">
            <input
              name="address2"
              placeholder="Apartment, suite, etc."
              value={form.address2}
              onChange={handleChange}
              className="flex grow border p-3 mb-5 text-md rounded-md"
            />
          </div>

          <div className="flex gap-2">
            <div className="flex flex-col flex-1">
              <div className="text-gray-500 text-sm mb-2 font-normal font-['Inter'] leading-[18px]">
                City
              </div>
              <input
                name="city"
                placeholder="City"
                value={form.city}
                onChange={handleChange}
                required
                className="border p-3 mb-5 text-md rounded-md"
              />
            </div>
            <div className="flex flex-col w-24">
              <div className="text-gray-500 text-sm mb-2 font-normal font-['Inter'] leading-[18px]">
                State
              </div>
              <input
                name="state"
                placeholder="State"
                value={form.state}
                onChange={handleChange}
                required
                className="border p-3 mb-5 text-md rounded-md"
              />
            </div>
          </div>

          <div className="flex gap-2">
            <div className="flex flex-col flex-1">
              <div className="text-gray-500 text-sm mb-2 font-normal font-['Inter'] leading-[18px]">
                ZIP / Postcode
              </div>
              <input
                name="zip"
                placeholder="ZIP"
                value={form.zip}
                onChange={handleChange}
                required
                className="border p-3 mb-5 text-md rounded-md"
              />
            </div>
            <div className="flex flex-col flex-1">
              <div className="text-gray-500 text-sm mb-2 font-normal font-['Inter'] leading-[18px]">
                Country
              </div>
              <input
                name="country"
                placeholder="Country"
                value={form.country}
                onChange={handleChange}
                required
                className="border p-3 mb-5 text-md rounded-md"
              />
            </div>
          </div>

          {submitError && (
            <p className="text-sm text-red-500 text-center mb-3 font-Inter">{submitError}</p>
          )}
          <div className="flex gap-3 w-full">
            <button
              type="button"
              onClick={() => setScreen("celebrate")}
              className="flex-1 eai-btn text-sm font-Inter"
            >
              Back
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 eai-btn-grn text-sm font-Inter disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? "Submitting..." : "Submit address"}
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <ModalHeader
        title="You're all set!"
        icon={<GiftIcon className="h-5 w-5 text-eai-primary-accent" style={{ "--stroke-0": "currentColor" } as any} />}
        onClose={handleClose}
      />
      <div className="flex grow flex-col p-5 items-center text-center gap-4">
        <div className="text-5xl">📬</div>
        <p className="text-sm text-gray-500 max-w-xs">
          We've got your address. We'll be in touch once your hat ships.
        </p>
        <button
          onClick={handleClose}
          className="w-full eai-btn-grn my-1 text-sm font-Inter"
        >
          Done
        </button>
      </div>
    </div>
  );
};

export default HatUnlockModal;
